/* eslint-disable @angular-eslint/directive-selector */
import type {
    EmbeddedViewRef } from '@angular/core';
import {
    Directive,
    Input,
    TemplateRef,
    ViewContainerRef,
    ɵstringify as stringify,
} from '@angular/core';
import { PermissionsService } from '../permissions.service';

export class IfHasPermissionContext {
    public $implicit: any = null;
    public ifHasPermission: any = null;
}

@Directive({ selector: '[ifHasPermission]' })
export class IfHasPermissionDirective {
    private _context = new IfHasPermissionContext();
    private _thenTemplateRef: TemplateRef<IfHasPermissionContext> | null = null;
    private _elseTemplateRef: TemplateRef<IfHasPermissionContext> | null = null;
    private _thenViewRef: EmbeddedViewRef<IfHasPermissionContext> | null = null;
    private _elseViewRef: EmbeddedViewRef<IfHasPermissionContext> | null = null;

    constructor(private _viewContainer: ViewContainerRef,
                templateRef: TemplateRef<IfHasPermissionContext>,
                private permissionsService: PermissionsService
    ) {
        this._thenTemplateRef = templateRef;
    }

    /**
     * The Boolean expression to evaluate as the condition for showing a template.
     */
    @Input()
    set ifHasPermission(condition: string) {
        this._context.$implicit = this._context.ifHasPermission = this.permissionsService.can(condition);
        this._updateView();
    }

    /**
     * A template to show if the condition expression evaluates to true.
     */
    @Input()
    set ifHasPermissionThen(templateRef: TemplateRef<IfHasPermissionContext> | null) {
        assertTemplate('ifHasPermissionThen', templateRef);
        this._thenTemplateRef = templateRef;
        this._thenViewRef = null; // clear previous view if any.
        this._updateView();
    }

    /**
     * A template to show if the condition expression evaluates to false.
     */
    @Input()
    set ifHasPermissionElse(templateRef: TemplateRef<IfHasPermissionContext> | null) {
        assertTemplate('ifHasPermissionElse', templateRef);
        this._elseTemplateRef = templateRef;
        this._elseViewRef = null; // clear previous view if any.
        this._updateView();
    }

    private _updateView() {
        if (this._context.$implicit) {
            if (!this._thenViewRef) {
                this._viewContainer.clear();
                this._elseViewRef = null;

                if (this._thenTemplateRef) {
                    this._thenViewRef = this._viewContainer.createEmbeddedView(this._thenTemplateRef, this._context);
                }
            }
        } else if (!this._elseViewRef) {
                this._viewContainer.clear();
                this._thenViewRef = null;

                if (this._elseTemplateRef) {
                    this._elseViewRef = this._viewContainer.createEmbeddedView(this._elseTemplateRef, this._context);
                }
            }
    }

    /**
     * Assert the correct type of the expression bound to the `ifHasPermission` input within the template.
     *
     * The presence of this method is a signal to the Ivy template type check compiler that when the
     * `IfHasPermission` structural directive renders its template, the type of the expression bound to `ifHasPermission`
     * should be narrowed in some way. For `IfHasPermission`, it is narrowed to be non-null, which allows the
     * strictNullChecks feature of TypeScript to work with `IfHasPermission`.
     */
    // eslint-disable-next-line @typescript-eslint/naming-convention
    static ngTemplateGuard_ifHasPermission<E>(dir: IfHasPermissionDirective, expr: E): expr is NonNullable<E> {
        return true;
    }
}

function assertTemplate(property: string, templateRef: TemplateRef<any> | null): void {
    const isTemplateRefOrNull = !!(!templateRef || templateRef.createEmbeddedView);

    if (!isTemplateRefOrNull) {
        throw new Error(`${property} must be a TemplateRef, but received '${stringify(templateRef)}'.`);
    }
}
